<!--
	MultiRangeSlider
	================
	Two stacked `<input type="range">` thumbs over a styled track. Reports
	updates via `onvalueinput({ lower, upper })`.
-->
<script lang="ts" module>
	export type MultiRangeSliderChangeResult = {
		lower: number;
		upper: number;
	};
</script>

<script lang="ts">
	interface Props {
		lower: number;
		upper: number;
		min: number;
		max: number;
		step?: number;
		id?: string;
		/** Accessible name prefix for the two thumbs ("<label> minimum"/"maximum"). */
		label?: string;
		onvalueinput: (result: MultiRangeSliderChangeResult) => void;
	}

	const { lower, upper, min, max, step = 1, id = '', label = '', onvalueinput }: Props = $props();

	const span = $derived(max - min || 1);
	const lowerPercent = $derived(Math.max(((lower - min) / span) * 100, 0));
	const upperPercent = $derived(Math.min(((upper - min) / span) * 100, 100));
	const rangeLeft = $derived(`${lowerPercent}%`);
	const rangeWidth = $derived(`${Math.max(upperPercent - lowerPercent, 0)}%`);

	// Raise the lower thumb above the upper one when they're nearly adjacent
	// so the user can still grab it.
	const lowerOnTop = $derived(lower > upper - 100);

	// Keep the thumbs at least one unit apart.
	const onInput = (which: 'lower' | 'upper') => (event: Event) => {
		const target = event.currentTarget as HTMLInputElement;
		const raw = parseFloat(target.value);
		const value = which === 'lower' ? Math.min(raw, upper - 1) : Math.max(raw, lower + 1);
		onvalueinput({ lower, upper, [which]: value });
		target.value = String(value);
	};
</script>

<div class="container">
	<div class="rowContainer">
		<div class="minMaxLabel">{min}</div>
		<div class="sliderContainer">
			<input
				type="range"
				id={`${id}-lower`}
				{min}
				{max}
				{step}
				value={lower}
				aria-label={`${label} minimum`.trim()}
				oninput={onInput('lower')}
				class="thumb thumbZindex3"
				class:thumbZindex5={lowerOnTop} />
			<input
				type="range"
				id={`${id}-upper`}
				{min}
				{max}
				{step}
				value={upper}
				aria-label={`${label} maximum`.trim()}
				oninput={onInput('upper')}
				class="thumb thumbZindex4" />

			<div class="sliderTrackContainer">
				<div class="sliderTrack"></div>
				<div class="sliderRange" style:left={rangeLeft} style:width={rangeWidth}></div>
			</div>
		</div>
		<div class="minMaxLabel">{max}</div>
	</div>
	<div class="rangeLabels">
		<output for="{id}-lower">{lower}</output>
		<output for="{id}-upper">{upper}</output>
	</div>
</div>

<style>
	.container {
		--track-height: 0.5rem;
		/* Fits the largest number the MIRE app currently requires. */
		--minMaxLabelWidth: 37px;
		width: 100%;
	}

	.rowContainer {
		display: flex;
		justify-content: center;
		align-items: center;
		gap: var(--space-4);
	}

	.sliderContainer {
		position: relative;
		flex-grow: 1;
		isolation: isolate;
	}

	/* Two stacked native inputs; only their thumbs take pointer events. */
	.thumb {
		position: absolute;
		top: calc(var(--track-height) / 2);
		right: -5px;
		left: -5px;
		appearance: none;
		background: transparent;
		height: 0;
		pointer-events: none;
	}

	.thumbZindex3 {
		z-index: 3;
	}

	.thumbZindex4 {
		z-index: 4;
	}

	.thumbZindex5 {
		z-index: 5;
	}

	.sliderTrackContainer {
		border-radius: var(--radius);
		background: var(--border-strong);
		height: var(--track-height);
	}

	.sliderTrack,
	.sliderRange {
		position: absolute;
		width: 100%;
		height: var(--track-height);
	}

	.sliderTrack {
		z-index: 1;
	}

	.sliderRange {
		z-index: 2;
		border-radius: var(--radius);
		background-color: var(--accent);
	}

	.minMaxLabel {
		width: var(--minMaxLabelWidth);
		color: var(--text-muted);

		&:first-child {
			text-align: end;
		}
	}

	.rangeLabels {
		display: flex;
		justify-content: space-between;
		align-items: center;
		margin: var(--space-3) 0;
		padding: 0 calc(var(--minMaxLabelWidth) + var(--space-3));

		output {
			border: 1px solid var(--border-color);
			border-radius: var(--radius);
			background: var(--surface-alt);
			padding: var(--space-1) var(--space-2);
			font-variant-numeric: tabular-nums;
		}
	}

	/* Range-thumb pseudo-elements live in the UA shadow tree, so they must be unscoped; `.thumb` is unique to this component. */
	:global(.thumb::-webkit-slider-thumb) {
		position: relative;
		appearance: none;
		cursor: pointer;
		border: 1px solid var(--border-strong);
		border-radius: var(--radius-full);
		background-color: var(--surface);
		width: 1.25rem;
		height: 1.25rem;
		pointer-events: all;
	}

	:global(.thumb::-moz-range-thumb) {
		position: relative;
		appearance: none;
		cursor: pointer;
		border: 1px solid var(--border-strong);
		border-radius: var(--radius-full);
		background-color: var(--surface);
		width: 1.25rem;
		height: 1.25rem;
		pointer-events: all;
	}

	/* The input itself is zero-height, so the focus ring goes on the thumb. */
	:global(.thumb:focus-visible) {
		outline: none;
	}

	:global(.thumb:focus-visible::-webkit-slider-thumb) {
		outline: 2px solid var(--focus-ring);
		outline-offset: 2px;
	}

	:global(.thumb:focus-visible::-moz-range-thumb) {
		outline: 2px solid var(--focus-ring);
		outline-offset: 2px;
	}
</style>
